"use client"

import { router } from "@inertiajs/react"
import { FolderPlus } from "@untitledui/icons"
import { useMemo, useState } from "react"
import {
  DialogTrigger as AriaDialogTrigger,
  Heading as AriaHeading,
} from "react-aria-components"

import { Button } from "@/components/ui/button"
import { ButtonUtility } from "@/components/ui/button-utility"
import { CloseButton } from "@/components/ui/close-button"
import { Dialog, Modal, ModalOverlay } from "@/components/ui/modal"
import { Select } from "@/components/ui/select"
import { addPostToTrackPlaylistTrackPath } from "@/routes"
import type { Playlist, Track } from "@/types"

interface SelectMyPlaylistModalProps {
  playlists: Playlist[]
  playlist: Playlist
  track: Track
}

/**
 * Modal Selection Input My-Playlist component.
 * Adds the current track's post to a selected playlist (add_post_to_track).
 */
export default function SelectMyPlaylistModal({
  playlists,
  playlist,
  track,
}: SelectMyPlaylistModalProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedPlaylistId, setSelectedPlaylistId] = useState<string>("")
  const selectItems = useMemo(
    () =>
      playlists.map((playlist) => ({
        id: String(playlist.id),
        label: playlist.title,
      })),
    [playlists],
  )
  return (
    <AriaDialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <ButtonUtility
        size="xs"
        color="tertiary"
        tooltip="Add to My Playlist"
        icon={FolderPlus}
      />
      <ModalOverlay isDismissable>
        <Modal>
          <Dialog>
            <div className="bg-primary relative w-full overflow-hidden rounded-2xl shadow-xl sm:max-w-100">
              <CloseButton
                onClick={() => setIsOpen(false)}
                theme="light"
                size="sm"
                className="absolute top-0 right-0"
              />
              {playlists.length > 0 ? (
                <>
                  <div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
                    <div className="z-10 flex flex-col gap-0.5">
                      <AriaHeading
                        slot="title"
                        className="text-md text-primary font-semibold"
                      >
                        Add to playlist
                      </AriaHeading>
                      <p className="text-tertiary text-sm">
                        Choose a playlist from your library to add this track
                        to.
                      </p>
                    </div>
                  </div>
                  <div className="h-5 w-full" />
                  <div className="flex flex-col px-4 sm:px-6">
                    <Select
                      label="Playlist"
                      placeholder="Select a playlist"
                      size="md"
                      items={selectItems}
                      onChange={(key) =>
                        setSelectedPlaylistId(key != null ? String(key) : "")
                      }
                    >
                      {(item) => (
                        <Select.Item key={item.id} id={item.id}>
                          {item.label}
                        </Select.Item>
                      )}
                    </Select>
                  </div>
                  <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                    <Button
                      color="secondary"
                      size="lg"
                      onClick={() => setIsOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      size="lg"
                      onClick={() => {
                        if (!selectedPlaylistId) return
                        const url = addPostToTrackPlaylistTrackPath(
                          playlist.slug || playlist.id,
                          track.id,
                        )
                        router.post(url, {
                          target_playlist_id: selectedPlaylistId,
                        })
                        setIsOpen(false)
                      }}
                      disabled={!selectedPlaylistId}
                    >
                      Confirm
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
                    <div className="z-10 flex flex-col gap-0.5">
                      <AriaHeading
                        slot="title"
                        className="text-md text-primary font-semibold"
                      >
                        No playlists found!
                      </AriaHeading>
                      <p className="text-tertiary text-sm">
                        You don&apos;t have any playlists yet. Create one to add
                        this track to.
                      </p>
                    </div>
                  </div>
                  <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                    <Button
                      color="secondary"
                      size="lg"
                      onClick={() => setIsOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      size="lg"
                      onClick={() => {
                        setIsOpen(false)
                        router.visit("/playlists/new")
                      }}
                    >
                      Create New Playlist
                    </Button>
                  </div>
                </>
              )}
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </AriaDialogTrigger>
  )
}
